import { Box, CircularProgress, Dialog, DialogActions, DialogContent, useMediaQuery, useTheme } from "@mui/material"
import type { FC, ReactNode } from "react"

import { Button } from "@/components/common/Button"
import akrochemLogo from "@/images/akrochem_logo.svg"

interface EmailPreviewDialogProps {
  isSending: boolean
  onBack: () => void
  onSend: () => Promise<void> | void
  backLabel?: string
  sendLabel?: string
  /** Disables the Send action independent of isSending — e.g. while the
   * preview is still loading, or when the backend reports canSend: false. */
  sendDisabled?: boolean
  testid?: string
  children: ReactNode
}

export const emailPreviewStyles = {
  subject: { fontSize: 14, fontWeight: 600 },
  sender: { fontSize: 14 },
  body: { fontSize: 15 },
  bodyBold: { fontSize: 15, fontWeight: 700 },
  attachment: { fontSize: 16 },
} as const

export const EmailPreviewDialog: FC<EmailPreviewDialogProps> = ({
  isSending,
  onBack,
  onSend,
  backLabel = "Go Back",
  sendLabel = "Send",
  sendDisabled = false,
  testid = "email-preview-dialog",
  children,
}) => {
  const theme = useTheme()
  const isFullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const handleSendClick = () => {
    void onSend()
  }

  return (
    <Dialog open scroll='paper' fullScreen={isFullScreen} fullWidth maxWidth='md' data-testid={testid}>
      <DialogContent classes={{ root: "p-0" }}>
        <Box className='flex justify-center bg-gray-200 py-6'>
          <img src={akrochemLogo} alt='Akrochem' className='h-16' />
        </Box>

        {children}
      </DialogContent>
      <DialogActions className='justify-between bg-white px-10 py-4'>
        <Button appearance='outlined' disabled={isSending} onClick={onBack} data-testid={`${testid}.back-button`}>
          {backLabel}
        </Button>
        <Button
          appearance='contained'
          disabled={isSending || sendDisabled}
          onClick={handleSendClick}
          endIcon={
            isSending ? (
              <Box className='flex items-center'>
                <CircularProgress size={16} />
              </Box>
            ) : undefined
          }
          data-testid={`${testid}.send-button`}
        >
          {sendLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
